import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Loader2, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';

interface IssueTagSelectorProps {
  practitionerId: string;
  onUpdate: () => void;
}

interface IssueTag {
  id: string;
  tag_key: string;
  category: string;
  name_en: string;
  name_zh: string;
}

export default function IssueTagSelector({ practitionerId, onUpdate }: IssueTagSelectorProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all issue tags
  const { data: allTags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ['issue_tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('issue_tags')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data as IssueTag[];
    }
  });

  // Fetch practitioner's current issue tags
  const { data: practitionerTags = [], isLoading: practitionerTagsLoading } = useQuery({
    queryKey: ['practitioner_issue_tags', practitionerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('practitioner_issue_tags')
        .select('issue_tag_id')
        .eq('practitioner_id', practitionerId);
      
      if (error) throw error;
      return data.map(pt => pt.issue_tag_id);
    },
    enabled: !!practitionerId
  });

  // Initialize selected tags when practitioner tags load
  React.useEffect(() => {
    if (practitionerTags.length > 0 && selectedTagIds.length === 0) {
      setSelectedTagIds(practitionerTags);
    }
  }, [practitionerTags]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTagIds(prev => prev.filter(id => id !== tagId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Delete existing tags
      const { error: deleteError } = await supabase
        .from('practitioner_issue_tags')
        .delete()
        .eq('practitioner_id', practitionerId);

      if (deleteError) throw deleteError;

      // Insert new tags
      if (selectedTagIds.length > 0) {
        const { error: insertError } = await supabase
          .from('practitioner_issue_tags')
          .insert(
            selectedTagIds.map(tagId => ({
              practitioner_id: practitionerId,
              issue_tag_id: tagId
            }))
          );

        if (insertError) throw insertError;
      }

      toast({
        title: t('issueTags.success'),
        description: t('issueTags.successDescription'),
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating issue tags:', error);
      toast({
        title: t('issueTags.error'),
        description: t('issueTags.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedTags = allTags.filter(tag => selectedTagIds.includes(tag.id));
  
  // Group tags by category for display
  const groupedTags = React.useMemo(() => {
    const groups: Record<string, IssueTag[]> = {};
    allTags.forEach(tag => {
      if (!groups[tag.category]) {
        groups[tag.category] = [];
      }
      groups[tag.category].push(tag);
    });
    return groups;
  }, [allTags]);

  // Filter tags based on search
  const filteredTags = React.useMemo(() => {
    if (!searchQuery.trim()) return allTags;
    const query = searchQuery.toLowerCase();
    return allTags.filter(tag => 
      tag.name_en.toLowerCase().includes(query) ||
      tag.name_zh.toLowerCase().includes(query)
    );
  }, [allTags, searchQuery]);

  if (tagsLoading || practitionerTagsLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('issueTags.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('issueTags.description')}
          </p>

          {/* Popover Dropdown */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between border-amber-200"
              >
                <span className="text-muted-foreground">
                  {t('issueTags.searchPlaceholder')}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder={t('issueTags.searchPlaceholder')}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList className="max-h-96">
                  <CommandEmpty>{t('issueTags.noResults')}</CommandEmpty>
                  {Object.entries(groupedTags).map(([category, tags]) => {
                    const categoryTags = tags.filter(tag => 
                      filteredTags.some(ft => ft.id === tag.id)
                    );
                    
                    if (categoryTags.length === 0) return null;
                    
                    return (
                      <CommandGroup 
                        key={category} 
                        heading={t(`issueTags.categories.${category}`)}
                      >
                        {categoryTags.map(tag => (
                          <CommandItem
                            key={tag.id}
                            value={tag.name_en}
                            onSelect={() => {
                              handleToggleTag(tag.id);
                            }}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <div className={`h-4 w-4 border rounded flex items-center justify-center ${
                                selectedTagIds.includes(tag.id) 
                                  ? 'bg-amber-600 border-amber-600' 
                                  : 'border-gray-300'
                              }`}>
                                {selectedTagIds.includes(tag.id) && (
                                  <div className="h-2 w-2 bg-white rounded-sm" />
                                )}
                              </div>
                              <span className="flex-1">
                                {i18n.language === 'zh' ? tag.name_zh : tag.name_en}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Selected Tags Display */}
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-amber-50/50 rounded-md border border-amber-100">
              {selectedTags.map(tag => (
                <Badge 
                  key={tag.id} 
                  variant="secondary"
                  className="bg-amber-100 text-amber-900 hover:bg-amber-200"
                >
                  {i18n.language === 'zh' ? tag.name_zh : tag.name_en}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag.id)}
                    className="ml-1 hover:text-amber-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('issueTags.savingChanges')}
              </>
            ) : (
              t('issueTags.saveChanges')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
